import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useFirePostOfficeTrigger } from '@atlassian/post-office-context/post-office-trigger';

import type {
	CurrentPageTemplateData,
	TemplateXflowContext,
} from '@post-office/growth-common/confluence-experiments-context';
import { CANONICAL_TEMPLATE_KEYS } from '@post-office/growth-common/confluence-experiments-context/template-xflow';

import { watchContentHeaderTemplateData } from '@confluence/content-header/entry-points/watchContentHeaderTemplateData.ts';
import { expValEquals } from '@confluence/feature-experiments';
// eslint-disable-next-line no-restricted-imports -- this bridge has to read the current href outside route components
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { getPageDataFromUrl } from './getPageDataFromUrl.ts';

export const TEMPLATE_VIEW_TRIGGER_ID = 'twcg-2112-template-view-trigger';
export const TEMPLATE_APPLIED_TRIGGER_ID = 'twcg-2112-template-applied-trigger';

const TEMPLATE_XFLOW_EXPERIMENT = 'twcg-2112-twc-xflow-conf-pages-templates';

export type CanonicalTemplateKey =
	(typeof CANONICAL_TEMPLATE_KEYS)[keyof typeof CANONICAL_TEMPLATE_KEYS];

const canonicalTemplateKeyBySourceTemplateEntityId: Readonly<Record<string, CanonicalTemplateKey>> =
	{
		...Object.values(CANONICAL_TEMPLATE_KEYS).reduce<Record<string, CanonicalTemplateKey>>(
			(mapping, key) => ({ ...mapping, [key]: key }),
			{},
		),
		'ec8ecc7e-7e4b-4f8a-aa3f-a6a7f2df4ac9': CANONICAL_TEMPLATE_KEYS.MEETING_NOTES,
		'f7a9afa1-19c0-4558-abd4-f8060829f9fc': CANONICAL_TEMPLATE_KEYS.PROJECT_PLAN,
		'6873d89c-765f-4677-b370-048005c7470b': CANONICAL_TEMPLATE_KEYS.EOW_STATUS_REPORT,
		'7edaef4a-329f-47c9-abfe-7e241c9e17ba': CANONICAL_TEMPLATE_KEYS.DECISIONS,
		'be29e5eb-fbe7-4213-b16f-5fb25b13f2d8': CANONICAL_TEMPLATE_KEYS.REQUIREMENTS_RACI,
		'f877653e-87e1-4654-a915-3591b3dd0db3': CANONICAL_TEMPLATE_KEYS.BUSINESS_STATUS_UPDATE,
		'7c19a101-d13e-4d67-870b-279e6342c6bc': CANONICAL_TEMPLATE_KEYS.RECURRING_MEETING_MINUTES,
		'a2484be7-df58-4f17-a347-2fecb7b22bb9': CANONICAL_TEMPLATE_KEYS.PROJECT_STATUS,
	};

export const getCanonicalTemplateKey = (
	sourceTemplateEntityId: string | null | undefined,
): CanonicalTemplateKey | undefined =>
	sourceTemplateEntityId
		? canonicalTemplateKeyBySourceTemplateEntityId[sourceTemplateEntityId]
		: undefined;

const isPaidEdition = (edition: string | null | undefined) =>
	edition === 'STANDARD' || edition === 'PREMIUM';

type ExperimentEligibilityState = {
	contentId: string;
	isVariation: boolean;
};

/**
 * Supplies the current template to Post Office consumers and owns both boolean trigger lifecycles.
 *
 * The experiment is exposed once per current-page load. Only variation makes the shared template
 * eligibility boolean true; control and unevaluated pages retain the default false value.
 *
 * The cache-backed view path reflects creation provenance only. Applying a later template does not
 * persist sourceTemplateEntityId, so refresh can return the original key or no key at all.
 */
export const useCurrentPageTemplateData = (): TemplateXflowContext => {
	const routeContext = useContext(RoutesContext);
	const href = routeContext.getHref() ?? '';
	const { contentId } = getPageDataFromUrl(href);
	const { edition, isLicensed } = useSessionData();
	const { fireTrigger: fireTemplateViewTrigger } = useFirePostOfficeTrigger({
		triggerId: TEMPLATE_VIEW_TRIGGER_ID,
	});
	const { fireTrigger: fireTemplateAppliedTrigger } = useFirePostOfficeTrigger({
		triggerId: TEMPLATE_APPLIED_TRIGGER_ID,
	});
	const [currentPageTemplateData, setCurrentPageTemplateData] = useState<CurrentPageTemplateData>({
		contentId,
		blueprintModuleCompleteKey: undefined,
		isEligible: false,
	});
	const [experimentEligibilityState, setExperimentEligibilityState] =
		useState<ExperimentEligibilityState>();
	const evaluatedContentIdRef = useRef<string>();
	const appliedTemplateContentIdRef = useRef<string | undefined>();
	const isVariation =
		contentId !== undefined && experimentEligibilityState?.contentId === contentId
			? experimentEligibilityState.isVariation
			: false;
	const hasEvaluatedCurrentPage =
		contentId !== undefined && experimentEligibilityState?.contentId === contentId;
	const hasPaidLicensedAccess = isPaidEdition(edition) && isLicensed;
	const exposedCurrentPageTemplateData = useMemo<CurrentPageTemplateData>(
		() =>
			currentPageTemplateData.contentId === contentId
				? currentPageTemplateData
				: {
						contentId,
						blueprintModuleCompleteKey: undefined,
						isEligible: false,
					},
		[contentId, currentPageTemplateData],
	);

	useEffect(() => {
		if (!contentId) {
			evaluatedContentIdRef.current = undefined;
			setExperimentEligibilityState(undefined);
			return;
		}

		if (evaluatedContentIdRef.current === contentId) {
			return;
		}

		evaluatedContentIdRef.current = contentId;
		setExperimentEligibilityState({
			contentId,
			isVariation: expValEquals(TEMPLATE_XFLOW_EXPERIMENT, 'cohort', 'variation'),
		});
	}, [contentId]);

	useEffect(() => {
		if (appliedTemplateContentIdRef.current !== contentId) {
			appliedTemplateContentIdRef.current = undefined;
		}
		fireTemplateViewTrigger({ isActive: false });
		fireTemplateAppliedTrigger({ isActive: false });
		setCurrentPageTemplateData({
			contentId,
			blueprintModuleCompleteKey: undefined,
			isEligible: false,
		});

		if (!contentId || !hasEvaluatedCurrentPage) {
			return;
		}

		const subscription = watchContentHeaderTemplateData(contentId, (sourceTemplateEntityId) => {
			if (appliedTemplateContentIdRef.current === contentId) {
				return;
			}

			const blueprintModuleCompleteKey = getCanonicalTemplateKey(sourceTemplateEntityId);
			const isTemplateEligible = hasPaidLicensedAccess && blueprintModuleCompleteKey !== undefined;
			const isVariantEligible = isVariation && isTemplateEligible;

			setCurrentPageTemplateData({
				contentId,
				blueprintModuleCompleteKey,
				isEligible: isVariantEligible,
			});
			fireTemplateViewTrigger({ isActive: isTemplateEligible });
		});

		return () => {
			subscription.unsubscribe();
			fireTemplateViewTrigger({ isActive: false });
			fireTemplateAppliedTrigger({ isActive: false });
		};
	}, [
		contentId,
		fireTemplateAppliedTrigger,
		fireTemplateViewTrigger,
		isVariation,
		hasEvaluatedCurrentPage,
		hasPaidLicensedAccess,
	]);

	const onTemplateApplied = useCallback(
		(blueprintModuleCompleteKey: string | null | undefined) => {
			const canonicalTemplateKey = getCanonicalTemplateKey(blueprintModuleCompleteKey);
			const isTemplateEligible = hasPaidLicensedAccess && canonicalTemplateKey !== undefined;
			const isVariantEligible = isVariation && isTemplateEligible;

			appliedTemplateContentIdRef.current = contentId;
			fireTemplateViewTrigger({ isActive: false });
			fireTemplateAppliedTrigger({ isActive: isTemplateEligible });
			setCurrentPageTemplateData({
				contentId,
				blueprintModuleCompleteKey: canonicalTemplateKey,
				isEligible: isVariantEligible,
			});
		},
		[
			contentId,
			isVariation,
			fireTemplateAppliedTrigger,
			fireTemplateViewTrigger,
			hasPaidLicensedAccess,
		],
	);

	return useMemo(
		() => ({
			currentPageTemplateData: exposedCurrentPageTemplateData,
			onTemplateApplied,
		}),
		[exposedCurrentPageTemplateData, onTemplateApplied],
	);
};
